import { action, computed, observable } from "mobx";
import { observer, Provider } from "mobx-react";
import * as React from "react";
import { PageNextButton } from "../Buttons/Pdf/PageNextButton";
import { PagePreviousButton } from "../Buttons/Pdf/PagePreviousButton";
import { TextDecreaseButton } from "../Buttons/Pdf/TextDecreaseButton";
import { TextEnlargeButton } from "../Buttons/Pdf/TextEnlargeButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import * as DocumentReader from "../DocumentReader";
import { Area } from "../General/Area";
import { AreaAside } from "../General/AreaAside";
import { AreaBody } from "../General/AreaBody";
import { FullScreen } from "../General/FullScreen";
import { Toolbar } from "../General/Toolbar";
import { ReaderToolbarMode, ReaderToolbarPart } from "../Reader/ReaderToolbarPart";
import * as AreaUiInfo from "../States/AreaUiInfo";
import { DialogStore } from "../Stores/DialogStore";
import { Strings } from "../Stores/StringsStore";
import * as UiStore from "../Stores/UiStore";
import { EPubFileSetting, EPubFileSettingsStore } from "./EPubFileSettingsStore";
import { EPubNavigationView } from "./EPubNavigationView";
import { EPubPageView } from "./EPubPageView";
import { EPubStore } from "./EPubStore";
import { SearchDocumentButton } from "../Buttons/Pdf/SearchDocumentButton";
import { SearchPreviousButton } from "../Buttons/Pdf/SearchPreviousButton";
import { SearchNextButton } from "../Buttons/Pdf/SearchNextButton";
import { Direction } from "./Direction";

export interface IEPubViewProps extends AreaUiInfo.IAreaUiInfo {
    id: string;
    docId: string;
    docLanguage: string;
    collectionId: string;
    downloadName: string;
    canDownload: boolean;
    fileName: string;
    originalFilename: string;
    isDeleted: boolean;
}

class HtmlTagInfo {
    elementStartIndex: number;
    elementEndIndex: number;
}

@observer
export class EPubView extends React.Component<IEPubViewProps, {}> {
    private iframe;
    private indexSearch: number = 0; // page intial index
    private searchInput: HTMLInputElement;
    @observable toggleSearchDoc: boolean = false;
    @observable showCountSpan: boolean = false;
    @observable disableMoveBtn: boolean = true;
    @observable totalTermCount: number = 0; // Total occurance of search term
    @observable isSearchResultEmpty: boolean = false;
    readonly areaUiInfo = new AreaUiInfo.AreaUiInfoImpl(UiStore.UiRepos.EPUB_UI_INFO);
    readonly store: EPubStore;
    epubResultModel: DocumentReader.EpubSearchResult = new DocumentReader.EpubSearchResult();
    epubSearchModel: DocumentReader.EpubSearchViewModel = new DocumentReader.EpubSearchViewModel();
    currentChapterIndex: number = -1;
    direction: Direction = Direction.Next;
    @observable currentCount: number = 0; //count shown in the top left corner
    chapterLinks: string[] = []; // Link for chapter
    isNavigating: boolean = true;
    chapterHTMLContent: string = "";
    htmlElementInfo: HtmlTagInfo[] = [];
    lastHighlightedPageIndex: number = -1;
    lastHighlightedSearchIndex: number = -1;
    newPageOnPreviousClick: boolean = false;
    newPageOnNextClick: boolean = false;
    searchInputValue: string = "";
    htmlFileList: string[];
    innerHTML: string = "";
    replaceString: string = "";
    currentCharIndex: number;
    inputText: HTMLFrameElement;//holds iFrame content    
    isStringPartOfWord: boolean = false;
    previousChar: string = null;

    constructor(props: IEPubViewProps) {
        super(props);
        this.store = new EPubStore(this.props.id);
        this.iframe = React.createRef();
    }

    @computed get findBarCss() {
        return !this.toggleSearchDoc ? { display: "none" } : null;
    }

    componentWillMount() {
        this.areaUiInfo.isCollapsed = UiStore.UiRepos.epubUiInfo.isCollapsed;
        this.areaUiInfo.asideWidth = UiStore.UiRepos.epubUiInfo.asideWidth;
    }

    @action setModel(chapters: DocumentReader.Chapter[]): void {
        this.store.rootChapters = chapters;
        let settings: EPubFileSetting = EPubFileSettingsStore.getSettings(this.store.id);
        if (settings) {
            this.store.zoom = settings.zoom;
            this.store.navigateTo(settings.target);
            this.store.scrollX = settings.scrollX;
            this.store.scrollY = settings.scrollY;
        }
        else {
            this.store.navigateTo(null);
            this.store.scrollX = 0;
            this.store.scrollY = 0;
        }
    }

    handleKeyDown = e => {
        if (e.key === " ") {
            e.preventDefault();
        }
    };

    // Handle word search in the document
    handleSearchDoc = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // ENTER: trigger search
        if (event.charCode === 13) {
            this.inputText = this.iframe.current as HTMLFrameElement;
            if (this.epubSearchModel.searchTerm && (this.epubSearchModel.searchTerm != this.searchInput.value)) {
                let hightLightStyleText: string = "<span id=\"searchText\" style=\"background-color: yellow;\">";
                let startIndex: number = this.inputText.contentWindow.document.body.innerHTML.indexOf(hightLightStyleText, 0);
                if (startIndex > -1) {
                    let endIndex: number = this.inputText.contentWindow.document.body.innerHTML.indexOf("</span>", startIndex) + 7;
                    this.replaceString = this.inputText.contentWindow.document.body.innerHTML.substring(startIndex, endIndex);
                    let actualString: string = this.replaceString.substr(this.replaceString.indexOf(">") + 1, this.epubSearchModel.searchTerm.length);
                    this.innerHTML = this.inputText.contentWindow.document.body.innerHTML.replace(this.replaceString, actualString);
                    this.inputText.contentWindow.document.body.innerHTML = this.innerHTML;
                }
            }

            this.epubSearchModel.epubDocumentGuid = this.props.id;
            this.epubSearchModel.searchTerm = this.searchInput.value;
            this.searchInputValue = this.searchInput.value;

            if (/^[A-Za-z0-9]*$/.test(this.epubSearchModel.searchTerm)) {
                DocumentReader.searchEpubIndexAsync(this.epubSearchModel)
                    .then(result => {
                        this.isNavigating = false;
                        this.epubResultModel = result;

                        if (result.count > 0) {
                            this.currentCount = 1;
                            this.indexSearch = 0;
                            this.showCountSpan = true;
                            this.totalTermCount = result.count;
                            this.isSearchResultEmpty = false;
                        }
                        else {
                            this.isSearchResultEmpty = true;
                            this.showCountSpan = false;
                            this.currentCount = 0;
                            this.totalTermCount = 0;
                            this.indexSearch = 0;
                        }
                        if (this.store.current) {
                            this.htmlFileList = result.targetChapterIds.map(t => {
                                return t.split("#")[0];
                            });
                            this.currentChapterIndex = this.htmlFileList.indexOf(this.store.current.split("#")[0]);
                            if (this.currentChapterIndex == 0) {
                                this.currentCount = 1;
                            }
                            this.chapterLinks = this.store.rootChapters.map((t) => {
                                return t.linkTarget
                            });

                            let indexOfCurrentChapter = this.chapterLinks.indexOf(this.store.current);
                            if (this.currentChapterIndex == -1) {
                                for (var chapter of result.targetChapterIds) {
                                    if (indexOfCurrentChapter < this.chapterLinks.indexOf(chapter)) {
                                        this.currentChapterIndex = result.targetChapterIds.indexOf(chapter);
                                        break;
                                    }
                                }
                                if (this.currentChapterIndex == -1) {
                                    this.currentChapterIndex = 0;
                                    this.currentCount = 1;
                                }
                                this.store.navigateTo(result.targetChapterIds[this.currentChapterIndex]);
                            }
                            else {
                                this.handlePageLoadCallback(false);
                            }
                        }
                    })
                    .catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
            }
            else {
                this.isSearchResultEmpty = true;
                this.showCountSpan = false;
                this.currentCount = 0;
                this.totalTermCount = 0;
                this.indexSearch = 0;
                DialogStore.show("", Strings.general.searchSpecialCharacterInEpub);
            }
        }
    }

    // Handle the next button click, highlight the search word with yellow color and update the search count accordingly.
    handleNextButtonClick = () => {
        if (this.htmlFileList.indexOf(this.store.current.split("#")[0]) == -1) {
            this.indexSearch = this.lastHighlightedSearchIndex;
            this.store.navigateTo(this.epubResultModel.targetChapterIds[this.lastHighlightedPageIndex]);
        }
        else {
            let index = this.findSearchStringInputInHtml(true);
            if (index >= 0) {
                this.showCountSpan = true;
                this.indexSearch = index + 1;
                if (this.htmlElementInfo.length == 0 ||
                    this.htmlElementInfo.filter(t => t.elementStartIndex < index && t.elementEndIndex > index).length == 0) {
                    this.innerHTML = this.innerHTML.substring(0, index) + this.replaceString + this.innerHTML.substring(index + this.searchInputValue.length);
                    this.inputText.contentWindow.document.body.innerHTML = this.innerHTML;
                    if (this.direction == Direction.Previous) {
                        this.direction = Direction.Next;
                        this.handleNextButtonClick();
                        return;
                    }
                    if (!this.newPageOnNextClick) {
                        this.currentCount += 1;
                    }
                    else {
                        this.newPageOnNextClick = false;
                    }
                    this.lastHighlightedPageIndex = this.currentChapterIndex;
                    this.lastHighlightedSearchIndex = index - 1;
                }
                else {
                    this.handleNextButtonClick();
                }
                this.disableMoveBtn = false;
            }
            else {
                this.disableMoveBtn = true;
                if (this.isStringPartOfWord && this.isCharacterLetterOrNumber(this.previousChar)) {
                    this.isStringPartOfWord = false;
                    this.previousChar = null;
                    this.indexSearch = this.currentCharIndex + 1;
                    this.handleNextButtonClick();

                }
                else if (this.currentChapterIndex < this.epubResultModel.targetChapterIds.length - 1) {
                    this.currentChapterIndex += 1;
                    this.indexSearch = 0;
                    this.store.navigateTo(this.epubResultModel.targetChapterIds[this.currentChapterIndex]);
                    this.currentCount += 1;
                }
                else if (this.currentChapterIndex == this.epubResultModel.targetChapterIds.length - 1) {
                    this.currentCount = 1;
                    this.currentChapterIndex = 0;
                    this.indexSearch = 0;
                    if (this.epubResultModel.targetChapterIds.length == 1) {
                        this.currentCount = 0;
                        this.disableMoveBtn = false;
                        this.handleNextButtonClick();
                    }
                    else {
                        this.store.navigateTo(this.epubResultModel.targetChapterIds[0]);
                    }

                }
            }
            this.applyScroll();
            this.direction == Direction.Next;
        }
    }

    // Handle the previous button click, highlight the search word with yellow color and update the search count accordingly.
    handlePreviousButtonClick = () => {
        if (this.htmlFileList.indexOf(this.store.current.split("#")[0]) == -1) {
            this.indexSearch = this.lastHighlightedSearchIndex;
            this.store.navigateTo(this.epubResultModel.targetChapterIds[this.lastHighlightedPageIndex]);
        }
        else {
            if (!this.isNavigating) {
                this.indexSearch -= 2; // Handle previous click after page naviation
            }
            let index = this.findSearchStringInputInHtml(false);
            this.isNavigating = true;
            if (index >= 0) {
                this.showCountSpan = true;
                this.indexSearch = index - 1;
                if (this.htmlElementInfo.length == 0 ||
                    this.htmlElementInfo.filter(t => t.elementStartIndex < index && t.elementEndIndex > index).length == 0) {
                    this.innerHTML = this.innerHTML.substring(0, index) + this.replaceString + this.innerHTML.substring(index + this.searchInputValue.length);
                    this.inputText.contentWindow.document.body.innerHTML = this.innerHTML;
                    if (this.direction == Direction.Next) {
                        this.direction = Direction.Previous;
                        if (!this.newPageOnPreviousClick) {
                            this.handlePreviousButtonClick();
                        }
                        else {
                            this.newPageOnPreviousClick = false;
                        }
                        return;
                    }
                    if (!this.newPageOnPreviousClick) {
                        this.currentCount -= 1;
                    }
                    else {
                        this.newPageOnPreviousClick = false;
                    }
                    this.lastHighlightedPageIndex = this.currentChapterIndex;
                    this.lastHighlightedSearchIndex = index + this.searchInputValue.length + 1;
                }
                else {
                    this.handlePreviousButtonClick();
                }
                this.disableMoveBtn = false;
            }
            else {
                this.disableMoveBtn = true;
                if (this.isStringPartOfWord && this.isCharacterLetterOrNumber(this.previousChar)) {
                    this.isStringPartOfWord = false;
                    this.previousChar = null;
                    this.indexSearch = this.currentCharIndex - 1;
                    this.handlePreviousButtonClick();
                }
                else if (this.currentChapterIndex != 0) {
                    this.currentChapterIndex -= 1;
                    this.store.navigateTo(this.epubResultModel.targetChapterIds[this.currentChapterIndex]);
                    this.currentCount -= 1;
                }
                else {
                    this.currentCount = this.epubResultModel.count;
                    this.currentChapterIndex = this.epubResultModel.targetChapterIds.length - 1;
                    if (this.epubResultModel.targetChapterIds.length == 1) {
                        this.indexSearch = this.innerHTML.length;
                        this.disableMoveBtn = false;
                        this.newPageOnPreviousClick = true;
                        this.handlePreviousButtonClick();
                    }
                    else {
                        this.store.navigateTo(this.epubResultModel.targetChapterIds[this.currentChapterIndex]);
                    }

                }
            }
            this.applyScroll();
            this.direction = Direction.Previous;
        }
    }

    // Find the search string in Html and returns its index.
    findSearchStringInputInHtml = (isNext: boolean): number => {
        this.isNavigating = true;
        let index: number;
        this.inputText = this.iframe.current as HTMLFrameElement;
        this.innerHTML = this.chapterHTMLContent;
        if (isNext) {
            index = this.innerHTML.toLowerCase().indexOf(this.searchInputValue.toLowerCase(), this.indexSearch);
        }
        else {
            index = this.innerHTML.toLowerCase().lastIndexOf(this.searchInputValue.toLowerCase(), this.indexSearch);
        }

        this.currentCharIndex = index;
        this.replaceString = "<span id='searchText' style='background-color: yellow;'>" + this.chapterHTMLContent.substr(index, this.searchInputValue.length) + "</span>";
        if (index != -1) {
            this.previousChar = this.innerHTML.toLowerCase().substr(index - 1, 1);
            if (this.isCharacterLetterOrNumber(this.previousChar)) {
                this.isStringPartOfWord = true;
                index = -1
            }
        }
        return index;
    }

    // Handle page load and set values
    handlePageLoadCallback = (isPageLoaded: boolean = true) => {
        this.inputText = this.iframe.current as HTMLFrameElement;
        if (isPageLoaded) {
            this.fetchHtmlElementAttributesAndComments(this.inputText);
        }
        if (this.toggleSearchDoc && this.searchInputValue != "") {
            this.currentChapterIndex = this.htmlFileList.indexOf(this.store.current.split("#")[0]);
            this.innerHTML = this.chapterHTMLContent;
            var index = 0;

            if (this.direction == Direction.Next) {
                this.getCurrentOccuranceofSearchString();
                index = this.findIndexOnChapterLoad(false);
            }
            else if (!this.isNavigating) {
                this.indexSearch = 0;
                this.getCurrentOccuranceofSearchString();
                index = this.findIndexOnChapterLoad(false);
            }
            else {
                this.indexSearch = this.innerHTML.length;
                index = this.findIndexOnChapterLoad(true);
            }

            if (index >= 0) {
                if (this.epubResultModel.targetChapters.length == 1) {
                    this.direction = Direction.Next;
                }
                this.showCountSpan = true;
                if (this.htmlElementInfo.length == 0 ||
                    this.htmlElementInfo.filter(t => t.elementStartIndex < index && t.elementEndIndex > index).length == 0) {
                    this.replaceString = "<span id='searchText' style='background-color: yellow;'>" + this.chapterHTMLContent.substr(index, this.searchInputValue.length) + "</span>";
                    this.innerHTML = this.innerHTML.substring(0, index) + this.replaceString + this.innerHTML.substring(index + this.searchInputValue.length);
                    this.inputText.contentWindow.document.body.innerHTML = this.innerHTML;
                    this.applyScroll();
                    this.lastHighlightedPageIndex = this.currentChapterIndex;
                    this.lastHighlightedSearchIndex = index - 1;
                }
                else {
                    this.handlePageLoadCallback();
                }
                this.disableMoveBtn = false;
            }
        }
    }

    //Find index on chapter load .
    findIndexOnChapterLoad = (isPrevious: boolean): number => {
        var index = 0;
        if (isPrevious) {
            index = this.innerHTML.toLowerCase().lastIndexOf(this.searchInputValue.toLowerCase(), this.indexSearch);
            this.previousChar = this.innerHTML.toLowerCase().substr(index - 1, 1);
            if (this.isCharacterLetterOrNumber(this.previousChar)) {
                this.indexSearch = index - 1;
                index = -1;
                this.newPageOnPreviousClick = true;
                this.handlePreviousButtonClick();
            }
            else if (index != -1) {
                this.indexSearch = index - 1;
            }
        }
        else {
            index = this.innerHTML.toLowerCase().indexOf(this.searchInputValue.toLowerCase(), this.indexSearch);
            this.previousChar = this.innerHTML.toLowerCase().substr(index - 1, 1);
            if (this.isCharacterLetterOrNumber(this.previousChar)) {
                this.indexSearch = index + 1;
                index = -1;
                this.newPageOnNextClick = true;
                this.handleNextButtonClick();
            }
            if (index != -1) {
                this.indexSearch = index + 1;
            }
        }

        return index;
    }

    // Apply scroll to search text
    applyScroll = () => {
        this.inputText.contentWindow.document.querySelector('#searchText').scrollIntoView({
            block: "center",
            behavior: 'smooth'
        });
    }

    // Fetch all elements in the document and avoid searching in element attributes, element closing, and HTML comments.
    fetchHtmlElementAttributesAndComments = (inputText: HTMLFrameElement) => {
        this.htmlElementInfo = [];
        this.chapterHTMLContent = inputText.contentWindow.document.body.innerHTML;
        //Avoid searching in element attributes
        var htmlTags = inputText.contentWindow.document.body.getElementsByTagName("*")
        let elementIndex: number = 0;
        for (var i = 0; i < htmlTags.length; i++) {
            let htmlAttribute: HtmlTagInfo = new HtmlTagInfo();
            htmlAttribute.elementStartIndex = this.chapterHTMLContent.indexOf(htmlTags[i].outerHTML, elementIndex);
            htmlAttribute.elementEndIndex = htmlAttribute.elementStartIndex + htmlTags[i].outerHTML.indexOf('>');
            this.htmlElementInfo.push(htmlAttribute);

            //Avoid searching in the element closing
            let closingTagIndex: number = htmlTags[i].outerHTML.toLowerCase().lastIndexOf('</', htmlTags[i].outerHTML.length);
            let closingTag: string = htmlTags[i].outerHTML.substr(closingTagIndex, htmlTags[i].outerHTML.length - closingTagIndex);
            let htmlCloseAttribute: HtmlTagInfo = new HtmlTagInfo();
            htmlCloseAttribute.elementStartIndex = this.chapterHTMLContent.indexOf(htmlTags[i].outerHTML, elementIndex) + closingTagIndex;
            htmlCloseAttribute.elementEndIndex = htmlCloseAttribute.elementStartIndex + closingTag.length;
            this.htmlElementInfo.push(htmlCloseAttribute);
            elementIndex = htmlAttribute.elementEndIndex;
        }

        //Avoid searching in html comments
        var comments = this.getAllComments(inputText.contentWindow.document.body);
        for (var i = 0; i < comments.length; i++) {
            let commentNode: string = "<!--" + comments[i];
            let cmtStartIndex: number = this.chapterHTMLContent.indexOf(commentNode);
            let cmtEndIndex: number = cmtStartIndex + commentNode.length + 3;
            let htmlAttribute: HtmlTagInfo = new HtmlTagInfo();
            htmlAttribute.elementStartIndex = cmtStartIndex;
            htmlAttribute.elementEndIndex = cmtEndIndex;
            this.htmlElementInfo.push(htmlAttribute);
        }
    }

    // Gets the search hit count.
    getCurrentOccuranceofSearchString = () => {
        let count: number = 1;
        let isFirstChapter: boolean = false;
        if (this.htmlFileList.indexOf(this.store.current.split("#")[0]) != -1) {
            for (var chapter of this.epubResultModel.targetChapters) {
                if (chapter.chapterTarget.split("#")[0] != this.store.current.split("#")[0]) {
                    if (this.currentChapterIndex != 0) {
                        count += chapter.occurance;
                    }
                    else {
                        isFirstChapter = true;
                        break;
                    }
                }
                else if (this.currentChapterIndex == 0) {
                    isFirstChapter = true;
                    break;
                }
                else {
                    break;
                }
            }
            if (!isFirstChapter) {
                this.currentCount = count;
            }
        }
    }

    // Invokes when a new chapter loaded .
    onNewchapterLoad = () => {
        this.isNavigating = false;
        if ((this.htmlFileList.indexOf(this.store.current.split("#")[0])) != -1 &&
            this.epubResultModel.targetChapterIds[this.lastHighlightedPageIndex].split("#")[0] != this.store.current.split("#")[0]) {
            this.currentCount = 1;
            this.indexSearch = 0;
        }
        if (this.htmlFileList.indexOf(this.store.current.split("#")[0]) == -1) {
            this.showCountSpan = false;
            this.disableMoveBtn = false;
        }
        else {
            this.totalTermCount = this.epubResultModel.count;
            this.showCountSpan = true;
        }
    }

    replaceAll(str, find, replace) {
        return str.replace(new RegExp(find, 'g'), replace);
    }

    isCharacterLetterOrNumber(char) {
        return (/[a-zA-Z]/).test(char) || (/^\d$/).test(char);
    }

    getAllComments(rootElem) {
        var comments = [];
        var iterator = document.createNodeIterator(rootElem, NodeFilter.SHOW_COMMENT);
        var curNode;
        while (curNode = iterator.nextNode()) {
            comments.push(curNode.nodeValue);
        }
        return comments;
    }

    componentDidMount() {
        this.areaUiInfo.selectCollapse();
        DocumentReader.getDocumentChaptersAsync(this.props.id)
            .then((chapters) => {
                this.setModel(chapters);
            })
            .catch(error => DialogStore.showError(Strings.general.errorDialogTitle, error));
    }

    @computed get areaAsideButtonTooltip() {
        return this.areaUiInfo.isCollapsed ? Strings.reader.readerAreaAsideButtonCollapsedTooltip : Strings.reader.readerAreaAsideButtonExpandedTooltip;
    }

    render() {
        let aside = <AreaAside>
            <Toolbar>
            </Toolbar>
            <EPubNavigationView chapterOpened={this.onNewchapterLoad} />
        </AreaAside>;

        return <Provider store={this.store} areaUiInfo={this.areaUiInfo}>
            <Area asideComponent={aside}>
                <AreaBody>
                    <FullScreen backgroundColor="white">
                        <Toolbar>
                            <span className="buttongroup is--buttongroup-left">
                                <ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
                                <SearchDocumentButton id="cusViewFind" on={null} pressed={null} onClick={() => { this.toggleSearchDoc = !this.toggleSearchDoc; }} />
                                <PagePreviousButton id="cusPrevious" disabled={this.store.disabledPrevious} onClick={this.store.navigatePrevious} />
                                <PageNextButton id="cusNext" disabled={this.store.disabledNext} onClick={this.store.navigateNext} />
                                <span className="toolbar__splitter"></span>
                                <TextDecreaseButton id="custZoomOut" disabled={this.store.disabledZoomOut} onClick={this.store.zoomOut} />
                                <TextEnlargeButton id="custZoomIn" disabled={this.store.disabledZoomIn} onClick={this.store.zoomIn} />
                            </span>
                            <ReaderToolbarPart
                                id={this.props.id}
                                docId={this.props.docId}
                                docLanguage={this.props.docLanguage}
                                collectionId={this.props.collectionId}
                                downloadName={this.props.downloadName}
                                canDownload={this.props.canDownload}
                                mode={ReaderToolbarMode.Default}
                                fileName={this.props.fileName}
                                extension=".epub"
                                isDeleted={this.props.isDeleted}
                                originalFilename={this.props.originalFilename}
                            />

                            <div className="findbar hidden doorHanger hiddenSmallView" id="cusFindbar" style={this.findBarCss}>

                                <span className="buttongroup is--buttongroup-left">
                                    <input ref={elem => this.searchInput = elem} id="cusFindInput" className="toolbarField" tabIndex={91} type="text" onKeyPress={this.handleSearchDoc} onKeyDown={this.handleKeyDown} />
                                    <SearchPreviousButton disabled={this.disableMoveBtn || this.totalTermCount == 0} id="cusFindPrevious" onClick={this.handlePreviousButtonClick} />
                                    <SearchNextButton disabled={this.disableMoveBtn || this.totalTermCount == 0} id="cusFindNext" onClick={this.handleNextButtonClick} />
                                </span>

                                <div>
                                    {
                                        this.totalTermCount != 0 ?
                                            <div className="findbar__additional">
                                                <span id="cusFindResultsCount" className="toolbarLabel">{this.currentCount + " out of " + this.totalTermCount}</span>
                                            </div>
                                            : this.isSearchResultEmpty ?
                                                <div className="findbar__additional">
                                                    <span id="cusFindResultsCount" className="toolbarLabel">{"0 out of 0"}</span>
                                                </div> : null
                                    }
                                </div>
                            </div>

                        </Toolbar>
                        <EPubPageView frameRef={this.iframe} pageLoaded={this.handlePageLoadCallback} />
                    </FullScreen>
                </AreaBody>
            </Area>
        </Provider>
    }
}